import { CrownFrame } from "@/components/CrownFrame";
import { useState } from "react";
import { toast } from "sonner";
import { Heart, Sword, Shield, Wind, Lock, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useGame } from "@/context/GameContext";
import { Bar } from "@/components/Bar";
import BoostBadges from "@/components/BoostBadges";
import MyUniqueLooks from "@/components/MyUniqueLooks";
import MyCollections from "@/components/MyCollections";
import { MwpCard } from "@/components/MwpBadge";
import WolfName from "@/components/WolfName";

const STAT = [["hp", "Health", Heart, "text-emerald-300"], ["atk", "Attack", Sword, "text-fuchsia-300"], ["def", "Defense", Shield, "text-sky-300"], ["spd", "Speed", Wind, "text-purple-300"]];
const ITEMS = { herb: "Healing Herb", atk_tonic: "Attack Tonic", def_tonic: "Defense Tonic", revive: "Revive Token" };

export default function Profile() {
  const { game, refresh } = useGame();
  const { wolf, stats, skills, available_skills, boosts, settings, looks } = game;
  const [eq, setEq] = useState(wolf.equipped);

  const toggle = (sid) => setEq((cur) => cur.includes(sid) ? cur.filter((x) => x !== sid) : cur.length < 3 ? [...cur, sid] : cur);
  const save = async () => { try { await api.post("/game/skills", { equipped: eq }); await refresh(); toast.success("Skills equipped"); } catch (e) { toast.error(errMsg(e)); } };
  const setLook = async (l) => { try { await api.post("/game/look", { look: l }); await refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const ownedLooks = Object.entries(looks).filter(([id, l]) => wolf.owned_looks.includes(id) || (l.pack_only && boosts.pack));

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[360px_1fr] gap-6">
      <section className="glass rounded-2xl overflow-hidden" data-testid="wolf-card">
        <CrownFrame tier={game.wallet?.mwp?.tier} className="rounded-t-2xl"><img src={game.avatar} alt={wolf.name} className="w-full aspect-square object-cover rounded-[inherit]" /></CrownFrame>
        <div className="p-6">
          <p className="text-xs font-mono-game text-slate-400">LEVEL {wolf.level} · {wolf.wins || 0}W / {wolf.losses || 0}L</p>
          <WolfName key={wolf.name} wolf={wolf} refresh={refresh} />
          {boosts.pack && <p className="text-amber-300 text-sm font-display mb-2">Pack Alpha</p>}
          <Bar label="XP" value={wolf.xp} max={wolf.xp_needed} color="#38BDF8" height="h-2" testid="profile-xp-bar" />
          {wolf.character && <p data-testid="profile-character" className="text-sm text-amber-300 mb-2">Playing as {wolf.character.name}</p>}
          <div className="mt-5"><BoostBadges boosts={boosts} settings={settings} art={game.badge_art} /></div>
          <MwpCard wallet={game.wallet} refresh={refresh} />
          <p className="text-[11px] font-mono-game text-slate-500 mt-6 mb-2">LOOK</p>
          <div className="flex gap-2 flex-wrap">
            {ownedLooks.map(([id, l]) => (
              <button key={id} data-testid={`profile-look-${id}`} onClick={() => setLook(id)} title={l.name}
                className={`w-12 h-12 rounded-lg overflow-hidden border-2 ${wolf.look === id ? "border-fuchsia-500" : "border-slate-700"}`}>
                <img src={l.image} alt={l.name} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="space-y-6">
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {STAT.map(([k, label, Icon, c]) => (
            <div key={k} className="glass rounded-2xl p-5" data-testid={`stat-${k}`}>
              <Icon className={`w-5 h-5 ${c} mb-4`} />
              <p className="font-mono-game text-3xl text-white">{stats[k]}</p>
              <p className="text-xs text-slate-400 mt-1">{label}{k === "hp" ? ` · now ${wolf.hp}` : ""}</p>
            </div>
          ))}
        </section>

        <section className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div><h2 className="font-display text-2xl text-amber-100">Skills</h2><p className="text-xs text-slate-400">Equip up to 3. <span className="font-mono-game">{eq.length}/3</span></p></div>
            <button data-testid="save-skills-btn" onClick={save} disabled={!eq.length} className="ember-btn px-5 py-2 rounded-full text-xs">SAVE LOADOUT</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {Object.entries(skills).map(([sid, s]) => {
              const unlocked = available_skills.includes(sid), on = eq.includes(sid);
              return (
                <button key={sid} data-testid={`skill-card-${sid}`} disabled={!unlocked} onClick={() => toggle(sid)}
                  className={`text-left p-4 rounded-xl border transition-colors ${on ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-slate-700 hover:border-slate-500"} ${s.nft && unlocked ? "nft-glow" : ""} disabled:opacity-40`}>
                  <span className="flex items-center justify-between font-semibold">
                    <span className="flex items-center gap-2">{!unlocked && <Lock className="w-3.5 h-3.5" />}{s.nft && <Sparkles className="w-3.5 h-3.5 text-amber-300" />}{s.name}</span>
                    <span className="font-mono-game text-xs text-blue-300">{s.cost}◆</span>
                  </span>
                  <span className="block text-xs text-slate-400 mt-1">{s.desc}</span>
                  {!unlocked && <span className="block text-[11px] text-amber-400/80 mt-1">{s.nft ? `Requires ${settings.collections.find((c) => c.key === s.nft).name} NFT` : `Unlocks at level ${s.level}`}</span>}
                </button>
              );
            })}
          </div>
        </section>

        <section className="glass rounded-2xl p-6" data-testid="inventory">
          <h2 className="font-display text-2xl text-amber-100 mb-4">Satchel</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(ITEMS).map(([k, n]) => (
              <div key={k} className="rounded-xl border border-slate-800 p-4" data-testid={`inventory-${k}`}>
                <p className="font-mono-game text-2xl">{wolf.inventory[k] || 0}</p><p className="text-xs text-slate-400">{n}</p>
              </div>
            ))}
          </div>
        </section>
        <MyUniqueLooks />
        <MyCollections wallet={game.wallet?.address} counts={game.wallet?.counts} />
      </div>
    </main>
  );
}
